'use client';
import type { ReactNode } from 'react';
import { ArrowUp } from '@/app/components/icons/ArrowUp';
import { Button } from '@/app/components/ui/button';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { FeatureGradient, GenerateNowText, SectionGradient, TOOL_INFO } from '../constants';
import type { ToolConfig, ToolType } from '../types';
import { Features } from './Features';
import { HeadVideo } from './HeadVideo';
import { Steps } from './Steps';

interface GenerateCardProps {
  title: string;
  description: string;
  index: number;
  clsName?: string;
  renderImageArea: () => ReactNode;
  imgClassName?: string;
  wrapperCls?: string;
}

function GenerateCard({
  title,
  description,
  index,
  clsName,
  renderImageArea,
  imgClassName,
  wrapperCls,
}: GenerateCardProps) {
  return (
    <div className={wrapperCls}>
      <div
        className={cn(
          'mx-auto flex min-h-[460px] max-w-[1440px] flex-row items-center gap-[90px] px-[100px] py-[60px] md:flex-col md:gap-0 md:px-4 md:py-[40px]',
          clsName,
        )}
      >
        <div className="flex-1 md:min-w-full">
          <h2 className="mb-[16px] font-bold text-4xl leading-[1.4] md:mb-[12px] md:text-center md:font-semibold md:text-2xl">
            {title}
          </h2>
          <p className="text-base text-gray-600 leading-[1.6] md:text-sm">{description}</p>
        </div>
        <div className={cn('flex flex-1 justify-center md:mt-6', index % 2 === 0 ? null : 'order-first md:order-last')}>
          <div className={cn('relative min-w-0 flex-1', imgClassName)}>
            {/* <div className="aspect-video"> */}
            {renderImageArea()}
            {/* </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FaqItemProps {
  question: string;
  answer: string;
}

function FaqItem({ question, answer }: FaqItemProps) {
  return (
    <details className="group border-gray-200 border-b py-10 md:py-6">
      <summary className="flex cursor-pointer items-center justify-between font-semibold text-2xl md:text-[16px]">
        {question}
        <ArrowUp className="transform transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <p className="mt-6 text-base text-gray-600 md:mt-4 md:text-sm">{answer}</p>
    </details>
  );
}

interface ProductTemplateProps {
  toolKey: ToolType;
  toolConfig: ToolConfig;
}

export default function ProductTemplate({ toolKey, toolConfig }: ProductTemplateProps) {
  const router = useRouter();

  const toolInfo = TOOL_INFO[toolKey];

  const features = toolInfo.features.map((feature, index) => {
    return {
      image: toolConfig.features[index],
      className: FeatureGradient[index],
      title: feature.title,
      description: feature.description,
    };
  });

  const generateSections = toolInfo.generates.map((grenrate, index) => {
    return {
      wrapperCls: SectionGradient[index],
      clsName: toolConfig.generates[index].className,
      title: grenrate.title,
      description: grenrate.description,
      imgClassName: toolConfig.generates[index].imgClassName,
      renderImageArea: () => {
        return (
          <img
            src={toolConfig.generates[index].src}
            alt={grenrate.title}
            width={546}
            height={307}
            loading="lazy"
            decoding="async"
          />
        );
      },
    };
  });

  const handleGenerateNowClick = () => {
    reportEvent('productpage_generate', {
      element_name: `${toolKey}_generate`,
    });
    router.push(toolConfig.generateNow);
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white">
      <HeadVideo toolKey={toolKey} toolConfig={toolConfig} clsName="flex items-center justify-center" />

      {/* Features Section */}
      <section className="bg-white">
        <Features features={features} className="mx-auto max-w-[1440px] py-[72px] md:py-[40px]" />
      </section>

      {/* Generate Sections */}
      {generateSections.map((section, index) => (
        <GenerateCard key={section.title} {...section} index={index} />
      ))}

      {/* How to Use Section */}
      <Steps toolKey={toolKey} toolConfig={toolConfig} />

      {/* FAQ Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] py-[72px] md:py-[40px]">
          <h2 className="mx-auto mb-8 px-[100px] text-center font-bold text-[40px] leading-[1.4] md:mb-2 md:px-4 md:font-semibold md:text-[28px]">
            {toolInfo.faqs.title}
          </h2>
          <div className="mx-auto px-[100px] md:px-4">
            {toolInfo.faqs.list.map((faq) => (
              <FaqItem key={faq.answer} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[linear-gradient(90deg,#E0DDFF_0%,#94D8FF_100%)]">
        <div className="mx-auto max-w-[1440px] py-[72px] text-center md:py-[40px]">
          <h2 className="mb-10 font-bold text-[40px] leading-normal md:mb-6 md:font-semibold md:text-[28px]">
            {toolInfo.generateNow.title}
          </h2>
          <Button
            variant="primary"
            className="text-white shadow-[0px_6px_20px_0px_rgba(3,157,255,0.40)]"
            style={{ background: 'linear-gradient(90deg, #5AE0FF 0%, #009BFF 100%)' }}
            size="xxl"
            onClick={handleGenerateNowClick}
          >
            {GenerateNowText}
          </Button>
        </div>
      </section>
    </div>
  );
}
